import { ROUTE_LOGIN } from '../routing/paths';

export type AgentIdentity = {
    agentCode: string;
    pro?: boolean;
    nom?: string;
    nom_agent?: string;
    prenom_agent?: string;
    email?: string;
    tel?: string;
    companyName?: string;
    companyAddress?: string;
    companyLogo?: string;
    e_attestation?: boolean;
    code_fournisseur?: string;
    campagne?: string;
    siret?: string;
};

export interface ErrorBody<T> {
    statusCode: number;
    error: string;
    errorId: string;
    message: string;
    details?: T;
}

export const isErrorBody = <T = unknown>(value: unknown): value is ErrorBody<T> => {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<ErrorBody<T>>;
    return typeof candidate.error === 'string' && typeof candidate.message === 'string' && typeof candidate.errorId === 'string';
};

type HttpMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type RequestOptions = {
    method?: HttpMethod;
    body?: unknown;
    signal?: AbortSignal;
    redirectOn401?: boolean;
};

const buildApiUrl = (path: string): string => {
    const trimmedPath = path.replace(/^\/+/, '');
    return `${process.env.REACT_APP_URL_API}/${trimmedPath}`;
};

const handleUnauthorized = (response: Response, redirectOn401: boolean) => {
    if (response.status === 401 && redirectOn401) {
        console.warn('401 detected – redirecting to login');
        window.location.href = ROUTE_LOGIN;
    }
};

const parseJsonSafely = async (response: Response) => {
    const text = await response.text();
    if (!text) return undefined;
    try {
        return JSON.parse(text);
    } catch (error) {
        console.error('Failed to parse JSON response', error);
        return undefined;
    }
};

export const requestJson = async <T = unknown>(path: string, options: RequestOptions = {}): Promise<T> => {
    const { method = 'POST', body, signal, redirectOn401 = true } = options;

    const response = await fetch(buildApiUrl(path), {
        method,
        credentials: 'include',
        headers: body instanceof FormData ? undefined : { 'Content-Type': 'application/json' },
        body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
        signal,
    });

    handleUnauthorized(response, redirectOn401);

    if (response.status === 204) {
        return undefined as unknown as T;
    }

    const data = await parseJsonSafely(response);

    if (!response.ok) {
        if (data) throw data;
        throw new Error(`Request failed with status ${response.status}`);
    }

    return data as T;
};

export const postJsonData = async <T = unknown>(path: string, body?: unknown, options: Omit<RequestOptions, 'body' | 'method'> = {}) => {
    return requestJson<T>(path, { ...options, body, method: 'POST' });
};

export const identify = async (agentCode: string, password: string): Promise<AgentIdentity> => {
    return postJsonData<AgentIdentity>(`identify?agentCode=${encodeURIComponent(agentCode)}&password=${encodeURIComponent(password)}`, undefined, {
        redirectOn401: false,
    });
};
